"use client";

import React, {createContext, useEffect, useRef, useState} from "react";
import {i18n} from "@/i18n-config";
import localForage from "localforage";
import pako from "pako";
import {usePathname} from "next/navigation";

export const TranslationContext = createContext({
    dictionary: {} as any,
    lang: i18n.defaultLocale
});

export const TranslationProvider = ({children}: {children: React.ReactNode}) => {
    const pathname = usePathname();
    const selectedLang = pathname?.split('/')[1];
    const lang = i18n.locales.includes(selectedLang) ? selectedLang : i18n.defaultLocale;
    const [dictionary, setDictionary] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const hasFetched = useRef(false);

    const getStorage = async (uniqueId: string) => {
        let res
        try {
            const cachedData = await localForage.getItem(uniqueId);
            res = pako.inflate(cachedData as pako.Data, {level: 6} as pako.InflateFunctionOptions)
            let parsedData = JSON.parse(new TextDecoder("utf-8").decode(res));

            if(parsedData.expiration < Date.now()){
                localForage.removeItem(uniqueId);
                await fetchDictionary();
            } else {
                setDictionary(parsedData.data);
            }
        } catch (e) {
            await fetchDictionary();
            throw e;
        }
    }

    const fetchDictionary = async () => {
        if (loading) return;
        setLoading(true);
        await fetch(`/api/dictionary/${lang}`, { next: { revalidate: 60 } }).then((res) => res.json()).then(async (data) => {
            setDictionary(data);
            data = new TextEncoder().encode(JSON.stringify(
                {
                    data: data,
                    expiration: Date.now() + (60 * 1000)
                }
            ));
            data = pako.deflate(data, {level: 6})
            // cache data
            localForage.setItem(`dictionary_${lang}`, data);
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        localForage.getItem(`dictionary_${lang}`).then(async (res) => {
            if (res) {
                await getStorage(`dictionary_${lang}`)
            } else {
                await fetchDictionary();
            }
        })
    }, [lang])

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [lang]);

    return <TranslationContext.Provider value={{dictionary, lang}}>
        {children}
    </TranslationContext.Provider>
}